import React, { useState, useEffect } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button, Modal, Form,
  Input, Select, message, Popconfirm, Avatar, Upload, Tooltip, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined, UserOutlined,
  DownloadOutlined, UploadOutlined, SearchOutlined, CloudSyncOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getDelegates, createDelegate, updateDelegate, deleteDelegate,
  bulkImportDelegates, getEvents, getCategories
} from '../api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const BADGE_OPTIONS  = ['Badge', 'Wristband'];
const STATUS_OPTIONS = ['active', 'inactive', 'blacklisted'];

const LOC_COLORS = ['#2563eb', '#16a34a', '#9333ea', '#d97706', '#dc2626', '#0891b2', '#7c3aed', '#059669'];

async function exportExcel(delegates) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Delegates');
  const headers = ['#', 'Name', 'RFID Tag', 'Badge Type', 'Category', 'Gender', 'Email', 'Phone', 'Company', 'Designation', 'City', 'Status', 'Current Location'];
  ws.addRow(headers);
  ws.getRow(1).fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1D6B' } };
  ws.getRow(1).font  = { bold: true, color: { argb: 'FFFFFFFF' } };
  delegates.forEach((d, i) => ws.addRow([i+1, d.delegate_name, d.rfid_tag, d.badge_type, d.category_name, d.gender, d.email, d.phone, d.company, d.designation, d.city, d.status, d.current_location || '']));
  ws.columns.forEach(c => { c.width = 20; });
  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf]), `delegates_${dayjs().format('YYYYMMDD')}.xlsx`);
}

function exportPDF(delegates) {
  const doc = new jsPDF({ orientation: 'landscape' });
  doc.setFontSize(14);
  doc.text('Delegate List', 14, 16);
  doc.setFontSize(9);
  doc.text(`Generated: ${dayjs().format('DD MMM YYYY HH:mm')} | Total: ${delegates.length}`, 14, 22);
  doc.autoTable({
    startY: 26,
    head: [['#', 'Name', 'RFID Tag', 'Category', 'Company', 'Email', 'Phone', 'Status']],
    body: delegates.map((d, i) => [i+1, d.delegate_name, d.rfid_tag, d.category_name, d.company || '', d.email || '', d.phone || '', d.status]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 29, 107] },
  });
  doc.save(`delegates_${dayjs().format('YYYYMMDD')}.pdf`);
}

export default function DelegateDashboard() {
  const navigate = useNavigate();
  const [delegates,    setDelegates]    = useState([]);
  const [events,       setEvents]       = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [filterEvent,  setFilterEvent]  = useState(null);
  const [filterCat,    setFilterCat]    = useState(null);
  const [search,       setSearch]       = useState('');
  const [form] = Form.useForm();

  const load = (evId, catId, q) => {
    setLoading(true);
    // Resolve effective filter values: explicit arg overrides state; null/undefined means "no filter"
    const ev  = evId  !== undefined ? evId  : filterEvent;
    const cat = catId !== undefined ? catId : filterCat;
    const sq  = q     !== undefined ? q     : search;
    const params = {};
    if (ev)  params.event_id    = ev;
    if (cat) params.category_id = cat;
    if (sq)  params.search      = sq;
    getDelegates(params)
      .then(r => setDelegates(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getEvents().then(r => {
      const evList = r.data.data || [];
      setEvents(evList);
      const active = evList.find(e => e.status === 'active');
      if (active) { setFilterEvent(active.id); load(active.id, null, ''); }
      else load();
    }).catch(() => load());
    getCategories().then(r => setCategories(r.data.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  const handleEventChange = (v) => { setFilterEvent(v); load(v, filterCat, search); };
  const handleCatFilter   = (id) => { const next = filterCat === id ? null : id; setFilterCat(next); load(filterEvent, next, search); };
  const handleSearch      = (v)  => { setSearch(v); load(filterEvent, filterCat, v); };

  // Category counts from current delegates list
  const catCounts = {};
  delegates.forEach(d => { if (d.category_id) catCounts[d.category_id] = (catCounts[d.category_id] || 0) + 1; });

  const openCreate = () => {
    setEditing(null); form.resetFields();
    form.setFieldsValue({ status: 'active', badge_type: 'Badge', gender: 'Male', country: 'India' });
    if (filterEvent) form.setFieldValue('event_id', filterEvent);
    setModalOpen(true);
  };

  const openEdit = (rec) => { setEditing(rec); form.setFieldsValue(rec); setModalOpen(true); };

  const handleSave = async () => {
    try {
      const vals = await form.validateFields();
      if (editing) { await updateDelegate(editing.id, vals); message.success('Delegate updated'); }
      else         { await createDelegate(vals);             message.success('Delegate registered'); }
      setModalOpen(false); load();
    } catch (e) {
      if (!e.errorFields) message.error(e.response?.data?.message || 'Save failed');
    }
  };

  const handleImportExcel = async (file) => {
    try {
      const wb  = new ExcelJS.Workbook();
      const buf = await file.arrayBuffer();
      await wb.xlsx.load(buf);
      const ws   = wb.worksheets[0];
      const rows = [];
      ws.eachRow((row, idx) => {
        if (idx === 1) return;
        const v = row.values;
        if (v[2]) rows.push({ delegate_name: v[2], rfid_tag: v[3], badge_type: v[4] || 'Badge', gender: v[5] || 'Male', email: v[6], phone: v[7], company: v[8], designation: v[9], event_id: filterEvent });
      });
      await bulkImportDelegates({ delegates: rows, event_id: filterEvent });
      message.success(`Imported ${rows.length} delegates`);
      load();
    } catch (e) { message.error('Import failed: ' + e.message); }
    return false;
  };

  const columns = [
    {
      title: '#', width: 44,
      render: (_, __, i) => <Text style={{ fontSize: 12, color: '#888' }}>{i + 1}</Text>
    },
    {
      title: 'DELEGATE', minWidth: 180,
      render: (_, d) => (
        <Space size={10}>
          <Avatar size={34}
            src={d.photo_url || undefined}
            style={{ background: d.category_color || '#2563eb', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
            {!d.photo_url && d.delegate_name?.[0]}
          </Avatar>
          <div style={{ lineHeight: 1.3 }}>
            <Text strong style={{ fontSize: 12, display: 'block' }}>{d.delegate_name}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{d.company || <span style={{ color: '#ccc' }}>—</span>}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'RFID TAG', dataIndex: 'rfid_tag', width: 190,
      render: v => <Text code style={{ fontSize: 10, letterSpacing: '0.5px' }}>{v}</Text>
    },
    {
      title: 'BADGE', dataIndex: 'badge_type', width: 88,
      render: v => <Tag style={{ borderRadius: 4, fontSize: 11 }}>{v}</Tag>
    },
    {
      title: 'CATEGORY', dataIndex: 'category_name', width: 105,
      render: (v, r) => v
        ? <Tag color={r.category_color} style={{ borderRadius: 4, fontSize: 11 }}>{v}</Tag>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'GENDER', dataIndex: 'gender', width: 80,
      render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text>
    },
    {
      title: 'EMAIL', dataIndex: 'email', width: 170,
      render: v => v ? <Text style={{ fontSize: 11 }}>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'PHONE', dataIndex: 'phone', width: 110,
      render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text>
    },
    {
      title: 'DESIGNATION', dataIndex: 'designation', width: 130,
      render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text>
    },
    {
      title: 'LOCATION', dataIndex: 'current_location', width: 130,
      render: (v, _, i) => v
        ? <Tag color={LOC_COLORS[i % LOC_COLORS.length]} style={{ borderRadius: 4, fontSize: 11 }}>{v}</Tag>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'STATUS', dataIndex: 'status', width: 80,
      render: v => (
        <Tag color={v === 'active' ? 'green' : v === 'inactive' ? 'default' : 'red'}
          style={{ borderRadius: 4, fontSize: 11 }}>
          {v}
        </Tag>
      )
    },
    {
      title: 'ACTIONS', width: 110, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="View Journey">
            <Button size="small" icon={<NodeIndexOutlined />}
              onClick={() => navigate(`/delegate-journey/${r.id}${r.event_id ? `?event_id=${r.event_id}` : ''}`)}
              style={{ borderRadius: 4 }} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}
              style={{ borderRadius: 4 }} />
          </Tooltip>
          <Popconfirm title="Delete delegate?" onConfirm={() =>
            deleteDelegate(r.id).then(() => { message.success('Deleted'); load(); })
          }>
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 4 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ background: '#f5f6fa', minHeight: '100vh', padding: '0 0 24px' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '16px 24px 12px' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
              <UserOutlined style={{ marginRight: 8 }} />Delegate Dashboard
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Register, manage and track conference delegates</Text>
          </Col>
          <Col>
            <Space wrap>
              <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleImportExcel}>
                <Button icon={<UploadOutlined />} style={{ borderRadius: 6 }}>Import Excel</Button>
              </Upload>
              <Button icon={<DownloadOutlined />} style={{ borderRadius: 6 }}
                onClick={() => exportExcel(delegates)}>
                Excel
              </Button>
              <Button icon={<DownloadOutlined />} style={{ borderRadius: 6 }}
                onClick={() => exportPDF(delegates)}>
                PDF
              </Button>
              <Button icon={<CloudSyncOutlined />} style={{ borderRadius: 6 }}>Sync Cloud</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
                style={{ background: '#0F1D6B', borderRadius: 6, fontWeight: 600 }}>
                Register Delegate
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        {/* Filters row */}
        <Row gutter={12} align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Select value={filterEvent} onChange={handleEventChange}
              style={{ width: 180, borderRadius: 6 }} placeholder="Select Event" allowClear>
              {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
            </Select>
          </Col>
          <Col>
            <Select placeholder="Category" allowClear style={{ width: 130 }}
              value={filterCat} onChange={v => { setFilterCat(v); load(filterEvent, v, search); }}>
              {categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
          </Col>
          <Col flex="1">
            <Input.Search
              placeholder="Search name / RFID..."
              onSearch={handleSearch}
              allowClear
              prefix={<SearchOutlined style={{ color: '#bbb' }} />}
              style={{ maxWidth: 300 }}
            />
          </Col>
          <Col>
            <Text strong style={{ color: '#0F1D6B', fontSize: 13 }}>
              Total Delegates: <span style={{ color: '#2563eb', fontSize: 16 }}>{delegates.length}</span>
            </Text>
          </Col>
        </Row>

        {/* Category chips */}
        {categories.length > 0 && (
          <Space wrap style={{ marginBottom: 14 }}>
            {categories.map(c => {
              const count = catCounts[c.id] || 0;
              const active = filterCat === c.id;
              return (
                <Tag
                  key={c.id}
                  color={active ? c.color : undefined}
                  onClick={() => handleCatFilter(c.id)}
                  style={{
                    cursor: 'pointer',
                    borderRadius: 20,
                    padding: '3px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    border: active ? `2px solid ${c.color}` : `1px solid ${c.color || '#ddd'}`,
                    color: active ? '#fff' : (c.color || '#333'),
                    background: active ? (c.color || '#2563eb') : '#fff',
                    userSelect: 'none',
                    transition: 'all 0.2s',
                  }}
                >
                  {c.name} {count > 0 && <span style={{ opacity: 0.8 }}>{count}</span>}
                </Tag>
              );
            })}
          </Space>
        )}

        {/* Table */}
        <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <Table
            columns={columns}
            dataSource={delegates}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 1400 }}
            pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} delegates` }}
            rowClassName={(_, i) => i % 2 === 0 ? '' : 'alt-row'}
            style={{ fontSize: 12 }}
          />
        </Card>
      </div>

      {/* Register / Edit Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined style={{ color: '#0F1D6B' }} />
            <span>{editing ? 'Edit Delegate' : 'Register Delegate'}</span>
          </Space>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={760}
        okText={editing ? 'Save Changes' : 'Register'}
        okButtonProps={{ style: { background: '#0F1D6B', borderRadius: 6 } }}
        cancelButtonProps={{ style: { borderRadius: 6 } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="middle">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="delegate_name" label="Full Name" rules={[{ required: true, message: 'Name required' }]}>
                <Input placeholder="Enter full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rfid_tag" label="RFID Tag / EPC" rules={[{ required: true, message: 'RFID tag required' }]}>
                <Input placeholder="E.g. E2806894000040051A11A955" style={{ fontFamily: 'monospace' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="event_id" label="Event" rules={[{ required: true }]}>
                <Select placeholder="Select event">
                  {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="category_id" label="Category">
                <Select placeholder="Select category" allowClear>
                  {categories.map(c => <Option key={c.id} value={c.id}><Tag color={c.color}>{c.name}</Tag></Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="badge_type" label="Badge Type">
                <Select>{BADGE_OPTIONS.map(b => <Option key={b} value={b}>{b}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="gender" label="Gender">
                <Select>{GENDER_OPTIONS.map(g => <Option key={g} value={g}>{g}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="email" label="Email">
                <Input type="email" placeholder="email@domain.com" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="9810000000" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company" label="Company / Organization">
                <Input placeholder="Company name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="designation" label="Designation">
                <Input placeholder="CEO, Reporter, etc." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="city" label="City">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="country" label="Country">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>{STATUS_OPTIONS.map(s => <Option key={s} value={s}>{s}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <style>{`
        .alt-row td { background: #fafbff !important; }
        .ant-table-thead > tr > th {
          background: #f0f3ff !important;
          color: #0F1D6B !important;
          font-weight: 700 !important;
          font-size: 11px !important;
          letter-spacing: 0.5px !important;
        }
      `}</style>
    </div>
  );
}
